import {
  Box,
  Flex,
  Icon,
  Select,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiArrowRight, FiCheck } from 'react-icons/fi';
import type { SnapshotCsvPreview, SnapshotStageBucketId } from 'services/snapshotsService';
import {
  fieldForHeader,
  type ColumnMappingState,
} from '../useSnapshotCsvMapping';
import { SnapshotCard, SnapshotPageSpinner } from './snapshotUi';

type SnapshotCsvMappingPanelProps = {
  file: File;
  preview: SnapshotCsvPreview | null;
  loading: boolean;
  columnMapping: ColumnMappingState;
  onAssignColumn: (header: string, fieldId: string) => void;
  stageMapping: Record<string, SnapshotStageBucketId>;
  onStageMappingChange: (value: string, bucket: SnapshotStageBucketId) => void;
  showFileMeta?: boolean;
};

export function SnapshotCsvMappingPanel({
  file,
  preview,
  loading,
  columnMapping,
  onAssignColumn,
  stageMapping,
  onStageMappingChange,
  showFileMeta = true,
}: SnapshotCsvMappingPanelProps) {
  const textColor = useColorModeValue('navy.700', 'white');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const selectBg = useColorModeValue('green.50', 'whiteAlpha.100');

  if (loading && !preview) {
    return <SnapshotPageSpinner />;
  }

  if (!preview) {
    return null;
  }

  const usedHeaders = new Set(Object.values(columnMapping));
  const unusedColumns = preview.columns
    .map((c) => c.header)
    .filter((header) => !usedHeaders.has(header));

  const missingRequired = (['url', 'company', 'lifecycleStage'] as const).filter(
    (id) => !columnMapping[id],
  );

  return (
    <Box>
      <SnapshotCard mb={6}>
        <Flex justify="space-between" align="flex-start" gap={4} mb={2} flexWrap="wrap">
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            We found these columns
          </Text>
          {showFileMeta && (
            <Text fontSize="sm" color={muted}>
              {preview.fileName || file.name} · {preview.rowCount} rows
            </Text>
          )}
        </Flex>
        <Text fontSize="sm" color={muted} mb={6} lineHeight="1.55">
          Here&apos;s how we read your file. Matching is on URL only. Anything we
          don&apos;t use is dropped before it&apos;s stored.
        </Text>

        {preview.rowImport && preview.rowImport.importableCount > 0 && (
          <Box mb={4} p={3} borderRadius="12px" bg="blue.50" fontSize="sm" color="navy.700">
            <Text fontWeight="600">
              We&apos;ll import {preview.rowImport.importableCount} account
              {preview.rowImport.importableCount === 1 ? '' : 's'}.
            </Text>
            {preview.rowImport.skippedCount > 0 && (
              <Text mt={1} color="gray.600">
                {preview.rowImport.skippedCount} row
                {preview.rowImport.skippedCount === 1 ? '' : 's'} will be skipped
                (missing fields or duplicate domains).
              </Text>
            )}
            {preview.rowImport.skipped.length > 0 && (
              <Text as="ul" mt={2} pl={4} color="gray.600" fontSize="xs">
                {preview.rowImport.skipped.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </Text>
            )}
          </Box>
        )}

        {preview.rowImport?.importableCount === 0 && (
          <Box mb={4} p={3} borderRadius="12px" bg="red.50" fontSize="sm" color="red.700">
            No rows can be imported with this mapping. Fix empty or duplicate rows in your
            file.
          </Box>
        )}

        {preview.warnings.length > 0 && (
          <Box mb={4} p={3} borderRadius="12px" bg="orange.50" fontSize="sm" color="orange.800">
            {preview.warnings.join(' ')}
          </Box>
        )}

        <Flex direction="column" gap={3}>
          {preview.columns.map((column) => {
            const selected = fieldForHeader(columnMapping, column.header);
            const isMapped = Boolean(selected);
            return (
              <Flex
                key={column.header}
                align="center"
                gap={3}
                direction={{ base: 'column', sm: 'row' }}
              >
                <Box flex="1" minW={0} w="100%">
                  <Text fontWeight="600" color={textColor} fontSize="sm">
                    {column.header}
                  </Text>
                  {column.sample && (
                    <Text fontSize="xs" color={muted} noOfLines={1}>
                      {column.sample}
                    </Text>
                  )}
                </Box>
                <Icon as={FiArrowRight} color={muted} display={{ base: 'none', sm: 'block' }} />
                <Flex align="center" gap={2} w={{ base: '100%', sm: '220px' }} flexShrink={0}>
                  <Select
                    size="sm"
                    borderRadius="12px"
                    bg={isMapped ? selectBg : undefined}
                    borderColor={borderColor}
                    value={selected}
                    onChange={(e) => onAssignColumn(column.header, e.target.value)}
                  >
                    <option value="">Don&apos;t use</option>
                    {preview.fields.map((field) => (
                      <option key={field.id} value={field.id}>
                        {field.label}
                        {field.required ? ' *' : ''}
                      </option>
                    ))}
                  </Select>
                  {isMapped && (
                    <Icon as={FiCheck} color="green.500" boxSize={4} flexShrink={0} />
                  )}
                </Flex>
              </Flex>
            );
          })}
        </Flex>

        {unusedColumns.length > 0 && (
          <Text fontSize="sm" color={muted} mt={5}>
            {unusedColumns.length} other column{unusedColumns.length === 1 ? '' : 's'} won&apos;t
            be used: {unusedColumns.join(', ')}.
          </Text>
        )}

        {missingRequired.length > 0 && (
          <Text fontSize="sm" color="red.500" mt={3}>
            Map {missingRequired.join(', ')} to continue.
          </Text>
        )}
      </SnapshotCard>

      {preview.stages.length > 0 && columnMapping.lifecycleStage && (
        <SnapshotCard>
          <Text fontSize="xl" fontWeight="bold" color={textColor} mb={1}>
            Sort your stages
          </Text>
          <Text fontSize="sm" color={muted} mb={6} lineHeight="1.55">
            We found {preview.stages.length} stage
            {preview.stages.length === 1 ? '' : 's'} in your file and made a first guess.
            Change any that are wrong.
          </Text>

          <Flex direction="column" gap={3}>
            {preview.stages.map((stage) => {
              const bucket = stageMapping[stage.value] || '';
              return (
                <Flex
                  key={stage.value}
                  align="center"
                  gap={3}
                  direction={{ base: 'column', sm: 'row' }}
                >
                  <Box flex="1" minW={0} w="100%">
                    <Text fontWeight="600" color={textColor} fontSize="sm">
                      {stage.value}
                    </Text>
                    <Text fontSize="xs" color={muted}>
                      {stage.count} account{stage.count === 1 ? '' : 's'}
                    </Text>
                  </Box>
                  <Icon as={FiArrowRight} color={muted} display={{ base: 'none', sm: 'block' }} />
                  <Flex align="center" gap={2} w={{ base: '100%', sm: '220px' }} flexShrink={0}>
                    <Select
                      size="sm"
                      borderRadius="12px"
                      bg={bucket ? selectBg : undefined}
                      borderColor={borderColor}
                      value={bucket}
                      onChange={(e) =>
                        onStageMappingChange(
                          stage.value,
                          e.target.value as SnapshotStageBucketId,
                        )
                      }
                    >
                      <option value="">Choose…</option>
                      {preview.stageBuckets.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.label}
                        </option>
                      ))}
                    </Select>
                    {bucket && (
                      <Icon as={FiCheck} color="green.500" boxSize={4} flexShrink={0} />
                    )}
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        </SnapshotCard>
      )}
    </Box>
  );
}
